// SCR-STAFF-883 — صف خطای ثبت (US-ACC-013): ردیف‌های ثبت‌نشده با علت (نقش بی‌نگاشت، تفصیلی ناموجود، دوره بسته، بدون قاعده، اصل ثبت‌نشده)،
// شمار به تفکیک علت، بازپردازش تکی و گروهی، اجرای دستی تجمیع و کنترل روزانه (AC-ACC-013.1، 013.2).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type AccPostingFailureReason, type AccSourceEntryView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { accKeys, Amount, EntryStatusChip, num, SCR_STAFF_883, useBranchName } from './common';
import { roleKey } from './MappingsPage';

const REASONS: readonly AccPostingFailureReason[] = [
  'UnmappedRole',
  'MissingDetailAccount',
  'Unbalanced',
  'NoRule',
  'PeriodClosed',
  'OriginalMissing',
];

export function PostingErrorsPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const branchName = useBranchName();
  const canManage = access.accessMode === 'Full' && access.can('acc.posting_errors.manage');
  const [reason, setReason] = useState<AccPostingFailureReason | ''>('');
  const [message, setMessage] = useState<string | null>(null);
  const errors = useInfiniteQuery({
    queryKey: accKeys.errors(session?.tenantId, reason),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/posting-errors', {
          params: { query: { reason: reason || undefined, cursor: pageParam, limit: 50 } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
  const reprocess = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/accounting/posting-errors:reprocess-all')),
    onSuccess: (data) => {
      setMessage(
        t('acc:errors.reprocessed', {
          resolved: faDigits(num(data.resolved)),
          processed: faDigits(num(data.processed)),
        }),
      );
      refresh();
    },
  });
  const reprocessOne = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/posting-errors/{id}:reprocess', { params: { path: { id } } }),
      ),
    onSuccess: (entry) => {
      setMessage(
        t('acc:errors.reprocessed', {
          resolved: faDigits(entry.status === 'Failed' ? 0 : 1),
          processed: faDigits(1),
        }),
      );
      refresh();
    },
  });
  const aggregate = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/accounting/aggregation:run', { body: { branchId: null, date: null } })),
    onSuccess: (data) => {
      setMessage(
        t('acc:errors.aggregation_done', {
          journals: faDigits(num(data.journals)),
          entries: faDigits(num(data.entries)),
          controls: faDigits(num(data.controls)),
          mismatches: faDigits(num(data.mismatches)),
        }),
      );
      refresh();
    },
  });
  // جزئیات «Role» یا «Role:parameter» (PostingService.ResolveLine).
  const unmappedLabel = (detail: string) => {
    const [role = detail, parameter] = detail.split(':');
    const label = t(roleKey(role), { defaultValue: role });
    return parameter ? `${label} (${parameter})` : label;
  };
  const counts = errors.data?.pages[0]?.counts ?? [];
  const columns: DataTableColumn<AccSourceEntryView>[] = [
    {
      id: 'date',
      headerKey: 'acc:journal.original_date',
      cell: (row) => <JalaliDate value={row.originalDate} />,
    },
    { id: 'branch', headerKey: 'acc:common.branch', cell: (row) => branchName(row.branchId) },
    { id: 'event', headerKey: 'acc:errors.event', cell: (row) => <bdi dir="ltr">{row.eventType}</bdi> },
    {
      id: 'document',
      headerKey: 'acc:journal.document',
      cell: (row) => <bdi dir="ltr">{row.documentNumber ?? row.documentType}</bdi>,
    },
    { id: 'amount', headerKey: 'acc:common.amount', cell: (row) => <Amount money={row.total} /> },
    {
      id: 'reason',
      headerKey: 'acc:errors.reason',
      cell: (row) => (
        <span data-testid="acc-error-reason" data-reason={row.failureReason ?? ''}>
          {row.failureReason ? t(`acc:reason.${row.failureReason}`) : '—'}
        </span>
      ),
    },
    {
      id: 'detail',
      headerKey: 'acc:errors.detail',
      cell: (row) =>
        row.failureReason === 'UnmappedRole' && row.failureDetail
          ? unmappedLabel(row.failureDetail)
          : (row.failureDetail ?? '—'),
    },
    { id: 'attempts', headerKey: 'acc:errors.attempts', cell: (row) => faDigits(num(row.attempts)) },
    { id: 'status', headerKey: 'acc:common.status', cell: (row) => <EntryStatusChip status={row.status} /> },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button
            size="small"
            onClick={() => reprocessOne.mutate(row.id)}
            disabled={reprocessOne.isPending}
            data-testid="acc-error-reprocess"
          >
            {t('acc:errors.reprocess')}
          </Button>
        ) : null,
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_883}
      titleKey="acc:errors.title"
      subtitleKey="acc:errors.subtitle"
      actions={
        canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => reprocess.mutate()}
              disabled={reprocess.isPending}
              data-testid="acc-error-reprocess-all"
            >
              {t('acc:errors.reprocess_all')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => aggregate.mutate()}
              disabled={aggregate.isPending}
              data-testid="acc-run-aggregation"
            >
              {t('acc:errors.run_aggregation')}
            </Button>
          </Stack>
        ) : undefined
      }
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            select
            size="small"
            label={t('acc:errors.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value as AccPostingFailureReason | '')}
            sx={{ minWidth: 220 }}
            slotProps={{ htmlInput: { 'data-testid': 'acc-error-filter' } }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {REASONS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:reason.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {counts.map((count) => (
              <Chip
                key={count.reason}
                size="small"
                label={`${t(`acc:reason.${count.reason}`)}: ${faDigits(num(count.count))}`}
                data-testid="acc-error-count"
                data-reason={count.reason}
              />
            ))}
          </Stack>
        </Stack>
      }
    >
      {message ? (
        <Alert
          severity="info"
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
          data-testid="acc-errors-message"
        >
          {message}
        </Alert>
      ) : null}
      {(reprocess.error ?? reprocessOne.error ?? aggregate.error) ? (
        <ErrorState error={reprocess.error ?? reprocessOne.error ?? aggregate.error} compact />
      ) : null}
      <DataTable
        captionKey="acc:errors.title"
        columns={columns}
        rows={errors.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={errors.isPending}
        error={errors.error}
        onRetry={() => void errors.refetch()}
        hasMore={errors.hasNextPage}
        onLoadMore={() => void errors.fetchNextPage()}
        isFetchingMore={errors.isFetchingNextPage}
        emptyTitleKey="acc:errors.empty"
      />
    </ListPage>
  );
}
